import type { BaseQueryFunction } from '../domain/interfaces/QueryClient';
import type { HttpClientForQuery } from '../domain/interfaces/HttpClientForQuery';
import { createAxiosHttpClient } from '../infra/adapters/axios.adapter';

/**
 * Base Query Function
 * -------------------
 * Utility function that creates a base query function for query libraries
 * Works with RTK Query, TanStack Query, or any query library
 * 
 * The HTTP client implementation can be swapped (Axios, fetch, ky, etc.)
 * by changing the httpClientFactory import
 */

/**
 * HTTP Client Factory
 * -------------------
 * Factory function to create HTTP client instance
 * Change this to swap HTTP client implementations (Axios -> fetch, ky, etc.)
 */
const httpClientFactory = (): HttpClientForQuery => {
  // Currently using Axios - can be swapped with:
  // - createFetchHttpClient() for native fetch
  // - createKyHttpClient() for ky library
  // - Any other HTTP client implementation
  
  // To test fetch adapter, uncomment the line below and comment out createAxiosHttpClient()
  // import { createFetchHttpClient } from '../infra/adapters/fetch.adapter.example';
  // return createFetchHttpClient();
  
  return createAxiosHttpClient();
};

/**
 * Base query function for query libraries
 * Converts HTTP client requests/responses to query library format
 * Returns a function that matches query library's base query contract
 */
export const baseQuery = (): BaseQueryFunction => {
  const httpClient = httpClientFactory();

  return async ({ url, method = 'GET', data, params, headers, withCredentials }) => {
    try {
      const response = await httpClient.request({
        url,
        method: method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        data,
        params,
        headers,
        withCredentials,
      });

      return { data: response.data };
    } catch (httpError) {
      const err = httpError as { status?: number; statusText?: string; data?: unknown; message?: string };
      return {
        error: {
          status: err.status,
          statusText: err.statusText,
          data: err.data || err.message || 'Unknown error',
        },
      };
    }
  };
};

